/**
 * Escopo de empresa da tela Meus Arquivos (admin): regras puras, sem React nem RTK Query.
 *
 * O escopo vive na URL (`?empresa=todas` ou `?empresa=<id>`), como a pasta (`?pasta=`), e a última
 * escolha fica na sessão do navegador (por aba e por usuário). Quem resolve a precedência
 * (URL > sessão > padrão) é o hook `useFileViewerCompany`; aqui ficam só as conversões.
 *
 * - Admin sem `empresa` na URL mas com `pasta`: é um favorito de antes do seletor, e abre em
 *   "Todas as empresas" (a visão própria), como abria.
 * - Cliente: sempre "todas". O backend ignora `empresa` para quem não é admin, e a tela não deve
 *   mostrar um escopo sem efeito.
 */

import { CompanyScope, FileListArgs } from 'helper/products/fileViewer';
import { isCompanyId } from 'helper/products/fileViewerCompanies';

/** Parâmetro da URL com o escopo. */
export const COMPANY_PARAM = 'empresa';

/** Valor do parâmetro para a visão própria (sem `empresa` na lista). */
const ALL_COMPANIES_PARAM = 'todas';

const STORAGE_PREFIX = 'meus-arquivos:empresa:v1';

/** Só dígitos: `Number(' 7')`, `Number('')` e `Number('1e3')` passariam sem isto. */
const DIGITS_ONLY = /^\d+$/;

/**
 * O que o admin vê ao abrir a tela sem escopo na URL nem na sessão: `'choose'` pede a escolha
 * (estado "Escolha uma empresa", sem pedir a lista); `'todas'` abre a visão própria. Decisão do
 * produto; trocar aqui muda a tela inteira.
 */
export type AdminDefaultScope = 'choose' | 'todas';
export const ADMIN_DEFAULT_SCOPE: AdminDefaultScope = 'choose';

/**
 * Escopo padrão do admin, a partir de `ADMIN_DEFAULT_SCOPE`.
 *
 * @returns `null` para "escolher" (a tela espera a escolha); `'todas'` para a visão própria.
 */
export function defaultAdminScope(): CompanyScope | null {
  return ADMIN_DEFAULT_SCOPE === 'todas' ? 'todas' : null;
}

/**
 * Lê `?empresa=` da URL.
 *
 * @param param - Valor de `empresa` na URL, ou `null` sem o parâmetro.
 * @param isAdmin - Papel: cliente sempre recebe `'todas'`.
 * @param hasFolderParam - A URL tem `?pasta=`: admin sem `empresa` mas com pasta abre em "todas".
 * @returns O escopo, ou `null` quando o admin não tem escopo na URL (ausente ou inválido).
 */
export function parseCompanyParam(
  param: string | null,
  isAdmin: boolean,
  hasFolderParam = false,
): CompanyScope | null {
  if (!isAdmin) return 'todas';
  if (param === null) return hasFolderParam ? 'todas' : null;
  if (param === ALL_COMPANIES_PARAM) return 'todas';
  if (!DIGITS_ONLY.test(param)) return null;
  const companyId = Number(param);
  return isCompanyId(companyId) ? { companyId } : null;
}

/**
 * Escreve o escopo para `?empresa=`.
 *
 * @param scope - Escopo escolhido.
 * @returns `'todas'` ou o id (`'7'`).
 */
export function serializeCompanyParam(scope: CompanyScope): string {
  return scope === 'todas' ? ALL_COMPANIES_PARAM : String(scope.companyId);
}

/**
 * Chave estável do escopo, para os efeitos que reagem à troca de empresa (como `folderKey`).
 *
 * @param scope - Escopo, ou `null` sem escolha.
 * @returns `'none'`, `'todas'` ou o id.
 */
export function companyScopeKey(scope: CompanyScope | null): string {
  return scope === null ? 'none' : serializeCompanyParam(scope);
}

/**
 * Argumentos de `useFileList` para o escopo.
 *
 * @param scope - Escopo, ou `null` sem escolha.
 * @param isAdmin - Papel: cliente nunca manda `empresa`.
 * @returns `{ empresa }` para admin com empresa escolhida; `{}` (a lista de hoje) no resto.
 */
export function toFileListArgs(scope: CompanyScope | null, isAdmin: boolean): FileListArgs {
  if (!isAdmin || scope === null || scope === 'todas') return {};
  return { empresa: scope.companyId };
}

/**
 * A lista não deve ser pedida: admin sem escolha (estado "Escolha uma empresa").
 *
 * @param scope - Escopo, ou `null` sem escolha.
 * @param isAdmin - Papel.
 */
export function shouldSkipFileList(scope: CompanyScope | null, isAdmin: boolean): boolean {
  return isAdmin && scope === null;
}

/**
 * Chave da sessão, uma por usuário: a escolha de outro login no mesmo navegador nunca é lida.
 *
 * @param userId - Id do usuário logado (`selectUserId`).
 */
export function companyStorageKey(userId: number): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

function toCompanyScope(value: unknown): CompanyScope | null {
  if (value === ALL_COMPANIES_PARAM) return 'todas';
  if (!value || typeof value !== 'object') return null;
  const { companyId } = value as Record<string, unknown>;
  return isCompanyId(companyId) ? { companyId } : null;
}

/**
 * Lê a última escolha da aba. Qualquer falha (sessão bloqueada, JSON inválido, formato errado)
 * vale como "nada salvo".
 *
 * @param userId - Id do usuário logado.
 * @returns O escopo salvo, ou `null`.
 */
export function readSessionScope(userId: number): CompanyScope | null {
  try {
    const raw = window.sessionStorage.getItem(companyStorageKey(userId));
    return raw ? toCompanyScope(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Guarda a escolha na aba. Sessão indisponível (modo privado, cota, bloqueio): a URL continua
 * sendo a fonte, e a escolha vale até sair da tela.
 *
 * @param userId - Id do usuário logado.
 * @param scope - Escopo escolhido.
 */
export function writeSessionScope(userId: number, scope: CompanyScope): void {
  try {
    window.sessionStorage.setItem(companyStorageKey(userId), JSON.stringify(scope));
  } catch {
    // Sem sessão: a URL já guarda o escopo desta tela
  }
}
